import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { PO_SHIPMENT_META } from "@/lib/poStatus";

const num = (n) => Math.round(n).toLocaleString();
const lbl = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";

export function PoShipmentDialog({ target, onOpenChange, onSaved }) {
  const po = target?.po;
  const initial = target?.shipment || null;
  const [f, setF] = useState({});
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!target) return;
    setF({
      carrier: initial?.carrier || po?.carrier || "", tracking: initial?.tracking || "",
      pickup_date: initial?.pickup_date || "", edd: initial?.edd || po?.edd || "",
      actual_delivery_date: initial?.actual_delivery_date || "",
      origin: initial?.origin || po?.origin || "", destination: initial?.destination || po?.destination || "",
      pallet_count: initial?.pallet_count ? String(initial.pallet_count) : "",
      freight_cost: initial?.freight_cost ? String(initial.freight_cost) : "",
      status: initial?.status || "not_shipped", notes: initial?.notes || "",
    });
    const q = {};
    if (initial) (initial.lines || []).forEach((l) => { q[l.po_line_id] = String(l.qty); });
    else (po?.lines || []).forEach((l) => { if (l.outstanding_ship > 0) q[l.id] = String(l.outstanding_ship); });
    setQty(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!po) return null;
  const anyQty = Object.values(qty).some((v) => Number(v) > 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f, pallet_count: Number(f.pallet_count) || 0, freight_cost: Number(f.freight_cost) || 0,
        lines: (po.lines || []).filter((l) => Number(qty[l.id]) > 0).map((l) => ({ po_line_id: l.id, qty: Number(qty[l.id]) })),
      };
      if (initial?.id) await api.put(`/inbound-shipments/${initial.id}`, payload);
      else await api.post(`/purchase-orders/${po.id}/shipments`, payload);
      toast.success(initial?.id ? "Shipment updated" : "Inbound shipment added");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save shipment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-xl" data-testid="po-shipment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{initial?.id ? `Edit ${initial.shipment_number}` : "New Inbound Shipment"}</DialogTitle>
          <DialogDescription>{po.po_number} · {po.supplier_name}. A PO can have multiple shipments.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className={lbl}>Items on this shipment</Label>
            {(po.lines || []).map((l, i) => (
              <div key={l.id} className="flex items-center gap-2 text-sm" data-testid={`po-ship-line-${i}`}>
                <span className="min-w-0 flex-1 truncate text-slate-700">{l.name}</span>
                <span className="whitespace-nowrap text-[10px] text-slate-400">{num(l.outstanding_ship)} awaiting shipment</span>
                <Input type="text" inputMode="decimal" value={qty[l.id] || ""} onChange={(e) => setQty((p) => ({ ...p, [l.id]: e.target.value.replace(/[^0-9.]/g, "") }))}
                  placeholder="0" className="h-8 w-24 rounded-sm text-right tabular-nums" data-testid={`po-ship-qty-${i}`} />
                <span className="w-12 text-xs text-slate-400">{l.unit}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className={lbl}>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="rounded-sm" data-testid="po-ship-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PO_SHIPMENT_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className={lbl}>Carrier</Label><Input value={f.carrier || ""} onChange={(e) => set("carrier", e.target.value)} className="rounded-sm" data-testid="po-ship-carrier" /></div>
            <div className="space-y-1"><Label className={lbl}>Tracking / PRO #</Label><Input value={f.tracking || ""} onChange={(e) => set("tracking", e.target.value)} className="rounded-sm" data-testid="po-ship-tracking" /></div>
            <div className="space-y-1"><Label className={lbl}>Pallet Count</Label><Input type="text" inputMode="decimal" value={f.pallet_count || ""} onChange={(e) => set("pallet_count", e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-sm text-right tabular-nums" data-testid="po-ship-pallets" /></div>
            <div className="space-y-1"><Label className={lbl}>Pickup Date</Label><Input type="date" value={f.pickup_date || ""} onChange={(e) => set("pickup_date", e.target.value)} className="rounded-sm" data-testid="po-ship-pickup-date" /></div>
            <div className="space-y-1"><Label className={lbl}>Expected Delivery</Label><Input type="date" value={f.edd || ""} onChange={(e) => set("edd", e.target.value)} className="rounded-sm" data-testid="po-ship-edd" /></div>
            <div className="space-y-1"><Label className={lbl}>Actual Delivery</Label><Input type="date" value={f.actual_delivery_date || ""} onChange={(e) => set("actual_delivery_date", e.target.value)} className="rounded-sm" data-testid="po-ship-add" /></div>
            <div className="space-y-1"><Label className={lbl}>Freight Cost</Label><Input type="text" inputMode="decimal" value={f.freight_cost || ""} onChange={(e) => set("freight_cost", e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-sm text-right tabular-nums" data-testid="po-ship-freight" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className={lbl}>Origin</Label><Textarea value={f.origin || ""} onChange={(e) => set("origin", e.target.value)} rows={2} className="rounded-sm text-xs" data-testid="po-ship-origin" /></div>
            <div className="space-y-1"><Label className={lbl}>Destination</Label><Textarea value={f.destination || ""} onChange={(e) => set("destination", e.target.value)} rows={2} className="rounded-sm text-xs" data-testid="po-ship-destination" /></div>
          </div>
          <div className="space-y-1"><Label className={lbl}>Notes</Label><Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-sm" data-testid="po-ship-notes" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={!anyQty || saving} onClick={save} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="po-ship-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{initial?.id ? "Save Shipment" : "Add Shipment"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
